import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import DashboardLayout from "./layouts/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import SSPListPage from "./pages/SSPListPage";
import SSPFormPage from "./pages/SSPFormPage";
import DSPListPage from "./pages/DSPListPage";
import DSPFormPage from "./pages/DSPFormPage";
import DSPHealthPage from "./pages/DSPHealthPage";
import CompanyListPage from "./pages/CompanyListPage";
import CompanyFormPage from "./pages/CompanyFormPage";
import BidSimulationPage from "./pages/BidSimulationPage";
import BulkImportPage from "./pages/BulkImportPage";
import FloorOptimizerPage from "./pages/FloorOptimizerPage";
import AlertsPage from "./pages/AlertsPage";
import ABTestingPage from "./pages/ABTestingPage";
import UsersPage from "./pages/UsersPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import SettingsPage from "./pages/SettingsPageAdvanced";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import PerformancePage from "./pages/PerformancePage";
import StatsPage from "./pages/StatsPage";
import BillingPage from "./pages/BillingPage";
import ReportingPage from "./pages/ReportingPage";
import ListManagerPage from "./pages/ListManagerPage";
import FootfallPage from "./pages/FootfallPage";
import AudiencePage from "./pages/AudiencePage";
import "./App.css";

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="analytics" element={<AnalyticsDashboardPage />} />
          <Route path="performance" element={<PerformancePage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="reporting" element={<ReportingPage />} />
          <Route path="list-manager" element={<ListManagerPage />} />
          <Route path="footfall" element={<FootfallPage />} />
          <Route path="audience" element={<AudiencePage />} />
          <Route path="ssps" element={<SSPListPage />} />
          <Route path="ssps/new" element={<SSPFormPage />} />
          <Route path="ssps/:id/edit" element={<SSPFormPage />} />
          <Route path="dsps" element={<DSPListPage />} />
          <Route path="dsps/new" element={<DSPFormPage />} />
          <Route path="dsps/:id/edit" element={<DSPFormPage />} />
          <Route path="dsp-health" element={<DSPHealthPage />} />
          <Route path="companies" element={<CompanyListPage />} />
          <Route path="companies/new" element={<CompanyFormPage />} />
          <Route path="companies/:id/edit" element={<CompanyFormPage />} />
          <Route path="simulation" element={<BidSimulationPage />} />
          <Route path="optimizer" element={<FloorOptimizerPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="ab-testing" element={<ABTestingPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="import" element={<BulkImportPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
